import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { 
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  PencilSquareIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { PMField } from "../../../../components/utils/PMField";
import { PromangRichTextEditor } from "../../../../components/utils/PromangRichTextEditor";
import { DocumentUploadSectionV6 } from "../../../../components/dashboard/Media/DocumentUploadSectionV6";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { documentDisciplines, getChaptersFromCategory, getSubChapters } from "../../../../utils/documentDisciplines";
import { getHseDocument, updateHseDocument, createHseDocument, getDocumentAccess, updateDocumentAccess } from "../../../../api/hse/documents";
import { fetchDocument } from "../../../../api/document";
import { getDisciplines } from "../../../../api/hse/disciplines";
import { DisciplineCategoryChapterSelector } from "../../../../components/dashboard/documents/DisciplineCategoryChapterSelector";
import { AIGenerator } from "../../../../components/utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";

const chapterNumbersMatch = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

const HSE_DOCUMENT_ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(", ");

const idsMatch = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

const findDisciplineByIdOrCode = (list, value) =>
  list.find((d) => idsMatch(d.id, value) || idsMatch(d.code, value));

const findCategoryByIdOrCode = (list, value) =>
  list.find((c) => idsMatch(c.id, value) || idsMatch(c.code, value));

const findCategoryAcrossDisciplines = (list, value) => {
  for (const discipline of list || []) {
    const category = findCategoryByIdOrCode(discipline.categories || [], value);
    if (category) return category;
  }
  return null;
};

/**
 * Find a chapter anywhere in the nested tree returned by disciplines API:
 * chapters[] -> sub_chapters[] -> sub_chapters[]
 */
const findChapterInCategoryTree = (category, chapterNumber) => {
  if (!category || !chapterNumber) return null;

  const walk = (chapters) => {
    if (!chapters || chapters.length === 0) return null;
    for (const ch of chapters) {
      if (chapterNumbersMatch(ch.number, chapterNumber)) return ch;
      const nested = ch.sub_chapters || ch.subChapters;
      const found = walk(nested);
      if (found) return found;
    }
    return null;
  };

  return walk(category.chapters || category.root_chapters);
};

/**
 * HSE/KS Document Edit - Create or edit document
 * Supports creating new documents and editing existing ones with version control
 */
export const HseKsDocumentEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  
  // Check if we're creating a revision (from URL params)
  const searchParams = new URLSearchParams(window.location.search);
  const isRevision = searchParams.get('revision') === 'true';
  
  // Track if URL params have been processed to avoid re-processing
  const urlParamsProcessedRef = React.useRef(false);
  
  const [document, setDocument] = useState({
    title: "",
    description: "",
    discipline: "prosjektering_vvs", // fag/bransje
    category: "", // category.id for selector matching
    category_code: "", // category_code (e.g., "kom", "hms") for backend validation
    chapter: "", // e.g., "6.1"
    sub_chapter: "", // e.g., "6.1.2"
    document_type: "procedure", // policy, procedure, instruction, form, guidance, record, template
    document_number: "", // Auto-generated: HMS-PRO-6.1.2
    content: "",
    content_type: "text", // "text" or "file" - om dokumentet skal ha tekstinnhold eller lastet opp fil
    attached_document_id: null, // ID til lastet opp dokument hvis content_type === "file"
    // status and version are handled by API, not stored in form state
    change_summary: "",
    access_type: "all", // 'all' or 'specific'
    selected_user_ids: [], // Array of user IDs for specific access
  });
  
  const [, setAvailableCategories] = useState([]);
  const [, setAvailableChapters] = useState([]);
  const [, setAvailableSubChapters] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);

  const canEdit = useUserHasAccess({ role: "hse_ks_documents", action: isNew ? "create" : "update" });

  const categoryCodes = {
    sys: "SYS",
    hms: "HMS",
    ks: "KS",
    lov: "LOV",
    mil: "MIL",
    for: "FOR",
    reg: "REG",
    kom: "KOM",
    ris: "RIS",
    hen: "HEN",
  };
  
  // Map legacy category.code values to hse_documents.category_code where they differ.
  // This mapping matches the backend mapping in HseDocumentsController
  const categoryCodeMap = {
    'HMS': 'hms',
    'KS': 'ks',
    'MIL': 'mil',
    'KVALITET': 'ks',
    'LEDELSE': 'sys',
    'AKAN': 'akan',
    'VVS_DESIGN': 'hms',
    'VENT_UTF': 'hms',
    'EL_UTF': 'hms',
    'EL_PRO': 'hms',
    'GLASS_UTF': 'hms'
  };
  
  const typeCodes = {
    policy: "POL",
    procedure: "PRO",
    instruction: "INS",
    form: "SKJ",
    guidance: "VEI",
    record: "REG",
    template: "MAL",
  };
  
  const generateDocumentNumber = () => {
    const parts = [];
    
    // Add category code
    if (document.category && categoryCodes[document.category]) {
      parts.push(categoryCodes[document.category]);
    }
    
    // Add document type code
    if (document.document_type && typeCodes[document.document_type]) {
      parts.push(typeCodes[document.document_type]);
    }
    
    // Add chapter (use sub_chapter if selected, otherwise chapter)
    if (document.sub_chapter) {
      parts.push(document.sub_chapter);
    } else if (document.chapter) {
      parts.push(document.chapter);
    }
    
    return parts.length > 0 ? parts.join("-") : "";
  };
  
  useEffect(() => {
    fetchDisciplines();
    fetchCompanyUsers();
    if (!isNew && id) {
      fetchDocument();
    }
  }, [id, isNew]);

  // Handle URL params for new documents after disciplines are loaded
  useEffect(() => {
    if (isNew && disciplines.length > 0 && !urlParamsProcessedRef.current) {
      const categoryParam = searchParams.get('category');
      const chapterParam = searchParams.get('chapter');
      const disciplineParam = searchParams.get('discipline');
      
      console.log('🔍 URL Params Debug:', {
        categoryParam,
        chapterParam,
        disciplineParam,
        disciplinesCount: disciplines.length,
        currentDocument: {
          discipline: document.discipline,
          category: document.category,
          chapter: document.chapter,
          category_code: document.category_code
        }
      });
      
      if (categoryParam || chapterParam || disciplineParam) {
        // Mark as processed to avoid re-processing
        urlParamsProcessedRef.current = true;
        
        // Determine which discipline to use
        let selectedDiscipline = null;
        let selectedDisciplineId = null;
        
        if (disciplineParam) {
          selectedDiscipline = disciplines.find((d) => 
            String(d.id) === String(disciplineParam) || String(d.code) === String(disciplineParam)
          );
          selectedDisciplineId = disciplineParam;
          console.log('📌 Setting discipline from param:', {
            disciplineParam,
            foundDiscipline: selectedDiscipline,
            disciplineId: selectedDisciplineId
          });
          handleChange("discipline", disciplineParam);
        } else if (categoryParam) {
          // If category is provided but no discipline, find which discipline contains it
          // Convert categoryParam to number for comparison if it's a numeric string
          const categoryParamNum = !isNaN(categoryParam) ? parseInt(categoryParam, 10) : null;
          
          selectedDiscipline = disciplines.find((d) => 
            d.categories?.some((c) => 
              String(c.id) === String(categoryParam) ||
              c.id === categoryParamNum ||
              c.code === categoryParam ||
              String(c.id) === categoryParam
            )
          );
          
          console.log('🔍 Searching for discipline with category:', {
            categoryParam,
            categoryParamNum,
            disciplines: disciplines.map(d => ({
              id: d.id,
              code: d.code,
              categories: d.categories?.map(c => ({ id: c.id, code: c.code }))
            }))
          });
          
          if (selectedDiscipline) {
            selectedDisciplineId = selectedDiscipline.id || selectedDiscipline.code;
            console.log('📌 Found discipline from category:', {
              categoryParam,
              foundDiscipline: selectedDiscipline,
              disciplineId: selectedDisciplineId
            });
            handleChange("discipline", selectedDisciplineId);
          } else {
            console.warn('⚠️ Could not find discipline for category:', {
              categoryParam,
              categoryParamNum,
              availableDisciplines: disciplines.map(d => ({
                id: d.id,
                code: d.code,
                categoryIds: d.categories?.map(c => c.id),
                categoryCodes: d.categories?.map(c => c.code)
              }))
            });
          }
        }
        
        // Set category and chapter after discipline is set
        // Use selectedDiscipline directly if available, otherwise wait for state update
        const setCategoryAndChapter = () => {
          // Use selectedDiscipline if available (most reliable)
          let currentDiscipline = selectedDiscipline;
          
          // If not available, try to find it from disciplines using the ID we set
          if (!currentDiscipline && selectedDisciplineId) {
            currentDiscipline = disciplines.find((d) => 
              String(d.id) === String(selectedDisciplineId) ||
              d.id === selectedDisciplineId ||
              d.code === selectedDisciplineId ||
              String(d.code) === String(selectedDisciplineId)
            );
          }
          
          // Fallback: try to find from document.discipline (may be stale)
          if (!currentDiscipline && document.discipline) {
            const disciplineToUse = disciplineParam || selectedDisciplineId || document.discipline;
            currentDiscipline = disciplines.find((d) => 
              String(d.id) === String(disciplineToUse) ||
              d.id === disciplineToUse ||
              d.code === disciplineToUse ||
              String(d.code) === String(disciplineToUse)
            );
          }
          
          console.log('⏰ Setting category/chapter - Finding discipline:', {
            disciplineParam,
            selectedDisciplineId,
            selectedDiscipline,
            documentDiscipline: document.discipline,
            foundDiscipline: currentDiscipline,
            disciplineId: currentDiscipline?.id,
            disciplineCode: currentDiscipline?.code,
            disciplineName: currentDiscipline?.name,
            allDisciplines: disciplines.map(d => ({ id: d.id, code: d.code, name: d.name }))
          });
          
          console.log('⏰ Setting category/chapter:', {
            categoryParam,
            chapterParam,
            currentDiscipline,
            disciplineCategories: currentDiscipline?.categories?.map(c => ({ id: c.id, code: c.code, name: c.name })),
            disciplineId: currentDiscipline?.id,
            disciplineCode: currentDiscipline?.code
          });
          
          if (!currentDiscipline) {
            console.error('❌ No discipline found:', {
              disciplineParam,
              selectedDisciplineId,
              documentDiscipline: document.discipline,
              availableDisciplines: disciplines.map(d => ({ id: d.id, code: d.code, name: d.name }))
            });
            return; // Exit early if no discipline found
          }
          
          if (categoryParam && currentDiscipline) {
            // Use EXACT same approach as DisciplineCategoryChapterSelector
            const searchId = String(categoryParam || '').toLowerCase().trim();
            const categoryObj = currentDiscipline.categories?.find((c) => {
              const cId = String(c.id || '').toLowerCase().trim();
              const cCode = String(c.code || '').toLowerCase().trim();
              
              // Exact match (case-insensitive) - same as DisciplineCategoryChapterSelector
              if (cId === searchId || cCode === searchId) {
                return true;
              }
              
              // Numeric match - EXACT same as DisciplineCategoryChapterSelector
              const cIdNum = parseInt(cId);
              const cCodeNum = parseInt(cCode);
              const searchNum = parseInt(searchId);
              
              if (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) {
                return true;
              }
              if (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum) {
                return true;
              }
              
              return false;
            });
            
            console.log('📋 Category object search:', {
              categoryParam,
              searchId,
              categoryObj,
              allCategories: currentDiscipline.categories?.map(c => {
                const cId = String(c.id || '').toLowerCase().trim();
                const cCode = String(c.code || '').toLowerCase().trim();
                const cIdNum = parseInt(cId);
                const cCodeNum = parseInt(cCode);
                const searchNum = parseInt(searchId);
                
                return { 
                  id: c.id, 
                  code: c.code,
                  name: c.name,
                  idType: typeof c.id,
                  cId,
                  cCode,
                  searchId,
                  matches: {
                    exactMatch: cId === searchId || cCode === searchId,
                    numericIdMatch: !isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum,
                    numericCodeMatch: !isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum,
                    anyMatch: (cId === searchId || cCode === searchId) ||
                             (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) ||
                             (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum)
                  }
                };
              })
            });
            
            if (categoryObj) {
              const categoryValue = categoryObj.id || categoryObj.code || categoryParam;
              console.log('✅ Setting category from URL param:', {
                categoryValue,
                categoryObj_id: categoryObj.id,
                categoryObj_code: categoryObj.code,
                categoryObj_name: categoryObj.name
              });
              handleChange("category", categoryValue);
              
              // Always derive category_code from the selected Category.code
              if (categoryObj.code) {
                handleChange("category_code", String(categoryObj.code).trim().toLowerCase());
              }
            } else {
              console.error('❌ Category object not found for param:', {
                categoryParam,
                searchId,
                currentDiscipline,
                availableCategories: currentDiscipline.categories?.map(c => ({ id: c.id, code: c.code, name: c.name }))
              });
            }
          }
          
          if (chapterParam) {
            console.log('📖 Setting chapter from URL param:', chapterParam);
            handleChange("chapter", chapterParam);
          }
        };
        
        // Try immediately if we have selectedDiscipline, otherwise wait for state update
        if (selectedDiscipline) {
          // Use a small delay to ensure handleChange has processed
          setTimeout(setCategoryAndChapter, 50);
        } else {
          // Wait longer if we need to find discipline from state
          setTimeout(setCategoryAndChapter, 300);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, disciplines.length]);
  
  // Ensure category and chapter are set when discipline becomes available (for URL params)
  useEffect(() => {
    if (isNew && disciplines.length > 0 && document.discipline) {
      const categoryParam = searchParams.get('category');
      const chapterParam = searchParams.get('chapter');
      
      // Only set if we have params and they're not already set
      if (categoryParam && !document.category) {
        // Find discipline
        const disciplineObj = disciplines.find((d) => 
          String(d.id) === String(document.discipline) || 
          d.code === document.discipline ||
          String(d.code) === String(document.discipline)
        );
        
        if (disciplineObj?.categories) {
          // Use EXACT same approach as DisciplineCategoryChapterSelector
          const searchId = String(categoryParam || '').toLowerCase().trim();
          const categoryObj = disciplineObj.categories.find((c) => {
            const cId = String(c.id || '').toLowerCase().trim();
            const cCode = String(c.code || '').toLowerCase().trim();
            
            if (cId === searchId || cCode === searchId) return true;
            
            const cIdNum = parseInt(cId);
            const cCodeNum = parseInt(cCode);
            const searchNum = parseInt(searchId);
            
            if (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) return true;
            if (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum) return true;
            
            return false;
          });
          
          if (categoryObj) {
            const categoryValue = categoryObj.id || categoryObj.code || categoryParam;
            console.log('🔄 useEffect - Setting category from URL param:', {
              categoryValue,
              categoryObj_id: categoryObj.id,
              categoryObj_code: categoryObj.code
            });
            handleChange("category", categoryValue);
            
            // Also set category_code
            if (categoryObj.code && categoryCodeMap[categoryObj.code]) {
              handleChange("category_code", categoryCodeMap[categoryObj.code]);
            }
          }
        }
      }
      
      if (chapterParam && !document.chapter) {
        console.log('🔄 useEffect - Setting chapter from URL param:', chapterParam);
        handleChange("chapter", chapterParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, disciplines, document.discipline, document.category, document.chapter]);
  
  const fetchCompanyUsers = async () => {
    try {
      // For new documents, try to get users from a dummy call or wait until document is created
      // For now, we'll handle this in fetchDocument for existing documents
      if (isNew) {
        // We can't fetch company users without a document ID
        // This will be handled after document creation
        setCompanyUsers([]);
      }
    } catch (err) {
      console.error("Error fetching company users:", err);
    }
  };

  const fetchDisciplines = async () => {
    try {
      const response = await getDisciplines();
      const loadedDisciplines = response.company_disciplines || [];
      console.log('📥 Fetched disciplines:', {
        count: loadedDisciplines.length,
        disciplines: loadedDisciplines.map(d => ({
          id: d.id,
          code: d.code,
          name: d.name,
          categoriesCount: d.categories?.length,
          categories: d.categories?.map(c => ({ id: c.id, code: c.code, name: c.name }))
        }))
      });
      setDisciplines(loadedDisciplines);
      
      // Set default discipline if none selected and disciplines available
      if (!document.discipline && loadedDisciplines.length > 0) {
        handleChange("discipline", loadedDisciplines[0].id || loadedDisciplines[0].code);
      }
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      // Fallback to static data
      setDisciplines(documentDisciplines);
    }
  };
  
  // Update available categories when discipline changes
  useEffect(() => {
    // Try to find discipline in loaded disciplines first, then fallback to static
    const discipline =
      findDisciplineByIdOrCode(disciplines, document.discipline) ||
      findDisciplineByIdOrCode(documentDisciplines, document.discipline);
    
    if (discipline) {
      setAvailableCategories(discipline.categories || []);
      // Reset category and chapters when discipline changes
      const categoryExists =
        findCategoryByIdOrCode(discipline.categories || [], document.category) ||
        findCategoryAcrossDisciplines(disciplines, document.category);
      if (document.category && !categoryExists) {
        handleChange("category", "");
        handleChange("chapter", "");
        handleChange("sub_chapter", "");
      }
    } else {
      setAvailableCategories([]);
    }
  }, [document.discipline, disciplines]);
  
  // Update available chapters when category changes
  useEffect(() => {
    if (document.discipline && document.category) {
      // Try to find discipline in loaded disciplines first, then fallback to static
      const discipline =
        findDisciplineByIdOrCode(disciplines, document.discipline) ||
        findDisciplineByIdOrCode(documentDisciplines, document.discipline);
      
      const category =
        findCategoryByIdOrCode(discipline?.categories || [], document.category) ||
        findCategoryAcrossDisciplines(disciplines, document.category);
      
      if (category) {
        const chapters = getChaptersFromCategory(category);
        setAvailableChapters(chapters);
        // Reset chapter and sub_chapter if current selection is invalid
        if (document.chapter && !chapters.some((number) => chapterNumbersMatch(number, document.chapter))) {
          handleChange("chapter", "");
          handleChange("sub_chapter", "");
        }
      } else {
        setAvailableChapters([]);
      }
    } else {
      setAvailableChapters([]);
    }
  }, [document.discipline, document.category, disciplines]);
  
  // Update available sub-chapters when chapter changes
  useEffect(() => {
    if (document.discipline && document.category && document.chapter) {
      const subChapters = getSubChapters(document.discipline, document.category, document.chapter);
      const subChapterNumbers = subChapters.map((sc) => sc.number);
      setAvailableSubChapters(subChapterNumbers);
      // Reset sub_chapter if current selection is invalid
      if (document.sub_chapter && !subChapterNumbers.includes(document.sub_chapter)) {
        handleChange("sub_chapter", "");
      }
    } else {
      setAvailableSubChapters([]);
    }
  }, [document.discipline, document.category, document.chapter]);
  
  // Auto-set category_code when discipline and category are set
  useEffect(() => {
    if (document.discipline && document.category && !document.category_code) {
      // Find discipline
      const disciplineObj = disciplines.find((d) => 
        String(d.id) === String(document.discipline) || 
        d.code === document.discipline ||
        String(d.code) === String(document.discipline)
      ) || documentDisciplines.find((d) => 
        String(d.id) === String(document.discipline) ||
        d.code === document.discipline
      );
      
      if (disciplineObj?.categories) {
        // Convert category to number if it's numeric
        const categoryNum = !isNaN(document.category) ? parseInt(document.category, 10) : null;
        
        // Find category - use EXACT same approach as DisciplineCategoryChapterSelector
        const searchId = String(document.category || '').toLowerCase().trim();
        const categoryObj = disciplineObj.categories.find((c) => {
          const cId = String(c.id || '').toLowerCase().trim();
          const cCode = String(c.code || '').toLowerCase().trim();
          
          // Exact match (case-insensitive) - same as DisciplineCategoryChapterSelector
          if (cId === searchId || cCode === searchId) {
            return true;
          }
          
          // Numeric match - EXACT same as DisciplineCategoryChapterSelector
          const cIdNum = parseInt(cId);
          const cCodeNum = parseInt(cCode);
          const searchNum = parseInt(searchId);
          
          if (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) {
            return true;
          }
          if (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum) {
            return true;
          }
          
          return false;
        });
        
        console.log('🔄 Auto-setting category_code:', {
          discipline: document.discipline,
          category: document.category,
          categoryType: typeof document.category,
          categoryObj,
          categoryObj_code: categoryObj?.code,
          categoryObj_id: categoryObj?.id,
          categoryCodeMap_hasMapping: categoryObj?.code ? categoryCodeMap[categoryObj.code] : false,
          disciplineObj,
          disciplineObj_id: disciplineObj?.id,
          disciplineObj_code: disciplineObj?.code,
          allCategories: disciplineObj?.categories?.map(c => ({
            id: c.id,
            idType: typeof c.id,
            code: c.code,
            codeType: typeof c.code,
            matches: {
              stringMatch: String(c.id) === String(document.category),
              numberMatch: c.id === categoryNum,
              directMatch: c.id === document.category,
              codeMatch: c.code === document.category,
              stringCodeMatch: String(c.code) === String(document.category)
            }
          }))
        });
        
        // Map category.code to category_code
        if (categoryObj?.code && categoryCodeMap[categoryObj.code]) {
          const mappedCode = categoryCodeMap[categoryObj.code];
          console.log('✅ Auto-mapped category_code:', {
            from: categoryObj.code,
            to: mappedCode
          });
          handleChange("category_code", mappedCode);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.discipline, document.category, disciplines]);
  
  useEffect(() => {
    // Auto-generate document number when discipline, category, type, or chapter changes
    if (document.discipline && document.category && document.document_type) {
      const generatedNumber = generateDocumentNumber();
      if (generatedNumber && generatedNumber !== document.document_number) {
        handleChange("document_number", generatedNumber);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.discipline, document.category, document.document_type, document.chapter, document.sub_chapter]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      setError(false);
      // Request draft version content if available (for editing)
      const response = await getHseDocument(id, true);
      const doc = response.document;
      
      // Fetch access info to get company users and current access settings
      let accessInfo = null;
      try {
        accessInfo = await getDocumentAccess(id);
        setCompanyUsers(accessInfo.all_company_users || []);
      } catch (err) {
        console.error("Error fetching access info:", err);
      }
      
      // Map API response to form state
      // IMPORTANT: category_code (e.g., "kom") is different from category.code (e.g., "AKAN")
      // We need to use category.id for matching in the selector
      // But we store category_code separately for backend submission
      const categoryCode = doc.category_code || doc.category?.code || "";
      // For selector matching, use category.id (most reliable)
      const categoryForSelector = doc.category?.id || doc.category?.code || categoryCode;
      
      // Extract chapter from document number if chapter is null
      // Document number format: CATEGORY-TYPE-CHAPTER (e.g., "KOM-VEI-2.2")
      // Always convert to string to ensure consistent format
      let chapterNumber = doc.chapter?.number ? String(doc.chapter.number) : "";
      if (!chapterNumber && doc.document_number) {
        // Try to extract chapter from document number
        // Split by "-" and take the last part(s) that look like a chapter number (e.g., "2.2", "6.1.2")
        const parts = doc.document_number.split("-");
        if (parts.length >= 3) {
          // Take the last part as chapter (e.g., "2.2")
          const lastPart = parts[parts.length - 1];
          // Check if it looks like a chapter number (contains digits and possibly dots)
          if (/^\d+(\.\d+)*$/.test(lastPart)) {
            chapterNumber = lastPart;
          }
        }
      }
      
      console.log('HseKsDocumentEdit - fetchDocument - API Response:', {
        doc,
        category_code: doc.category_code,
        category: doc.category,
        categoryCode,
        categoryForSelector,
        discipline: doc.discipline,
        chapter: doc.chapter,
        document_number: doc.document_number,
        extractedChapter: chapterNumber
      });
      
      setDocument({
        title: doc.title || "",
        description: doc.description || "",
        discipline: doc.discipline?.id || doc.discipline?.code || "",
        category: categoryForSelector, // Use category.id for selector matching
        category_code: categoryCode, // Store original category_code for backend submission
        chapter: chapterNumber,
        sub_chapter: "", // API doesn't return sub_chapter separately, it's part of chapter.number
        document_type: doc.document_type || "procedure",
        document_number: doc.document_number || "",
        content: doc.content || "",
        content_type: doc.content_type || "text",
        attached_document_id: doc.attached_document_id || null,
        // status and version are handled by API, not stored in form state
        change_summary: "",
        access_type:
          accessInfo?.access_scope === "all" ||
          (accessInfo?.access_scope == null &&
            accessInfo?.users_with_access?.length === accessInfo?.all_company_users?.length)
            ? "all"
            : "specific",
        selected_user_ids: accessInfo?.users_with_access?.map(u => u.user.id) || [],
      });
      
      console.log('HseKsDocumentEdit - fetchDocument - Set document state:', {
        discipline: doc.discipline?.id || doc.discipline?.code || "",
        category: categoryCode,
        chapter: chapterNumber,
      });
      
      // Set uploaded document if attached_document_id exists
      if (doc.attached_document_id) {
        if (doc.attached_document) {
          setUploadedDocument(doc.attached_document);
        } else {
          try {
            const fileResponse = await fetchDocument(doc.attached_document_id);
            setUploadedDocument(fileResponse?.data || { id: doc.attached_document_id });
          } catch {
            setUploadedDocument({ id: doc.attached_document_id });
          }
        }
      } else {
        setUploadedDocument(null);
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      setError(true);
      toast.error(t("Kunne ikke laste dokument"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setDocument(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!document.title.trim()) {
      toast.error(t("Tittel er påkrevd"));
      return;
    }

    if (!document.category) {
      toast.error(t("Kategori er påkrevd"));
      return;
    }

    if (!document.document_type) {
      toast.error(t("Dokumenttype er påkrevd"));
      return;
    }

    if (!document.chapter) {
      toast.error(t("Kapittel er påkrevd"));
      return;
    }

    // Validate content based on content_type
    if (document.content_type === "text" && !document.content.trim()) {
      toast.error(t("Dokumentinnhold er påkrevd når du velger å skrive innhold"));
      return;
    }

    if (document.content_type === "file" && !document.attached_document_id) {
      toast.error(t("Du må laste opp et dokument"));
      return;
    }

    try {
      setSaving(true);
      
      // Find category and chapter IDs from the selected values
      const disciplineObj = disciplines.find((d) => 
        (d.id === document.discipline) || (d.code === document.discipline)
      ) || documentDisciplines.find((d) => d.id === document.discipline);
      
      // Try to find category by code first (most common case), then by ID
      // Convert document.category to number if it's a numeric string
      const categoryParamNum = !isNaN(document.category) ? parseInt(document.category, 10) : null;
      let categoryObj = null;
      
      if (disciplineObj?.categories) {
        // Use EXACT same approach as DisciplineCategoryChapterSelector
        const searchId = String(document.category || '').toLowerCase().trim();
        categoryObj = disciplineObj.categories.find((c) => {
          const cId = String(c.id || '').toLowerCase().trim();
          const cCode = String(c.code || '').toLowerCase().trim();
          
          // Exact match (case-insensitive) - same as DisciplineCategoryChapterSelector
          if (cId === searchId || cCode === searchId) {
            return true;
          }
          
          // Numeric match - EXACT same as DisciplineCategoryChapterSelector
          const cIdNum = parseInt(cId);
          const cCodeNum = parseInt(cCode);
          const searchNum = parseInt(searchId);
          
          if (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) {
            return true;
          }
          if (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum) {
            return true;
          }
          
          return false;
        });
        
        const searchIdForLog = String(document.category || '').toLowerCase().trim();
        console.log('🔍 Searching for category in disciplineObj:', {
          document_category: document.category,
          document_category_type: typeof document.category,
          categoryParamNum,
          categoryParamNum_type: typeof categoryParamNum,
          searchId: searchIdForLog,
          categoryObj,
          allCategories: disciplineObj.categories.map(c => {
            const cId = String(c.id || '').toLowerCase().trim();
            const cCode = String(c.code || '').toLowerCase().trim();
            const cIdNum = parseInt(cId);
            const cCodeNum = parseInt(cCode);
            const searchNum = parseInt(searchIdForLog);
            
            const exactMatch = cId === searchIdForLog || cCode === searchIdForLog;
            const numericIdMatch = !isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum;
            const numericCodeMatch = !isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum;
            
            return { 
              id: c.id, 
              code: c.code,
              name: c.name,
              idType: typeof c.id,
              codeType: typeof c.code,
              cId,
              cCode,
              cIdNum,
              cCodeNum,
              searchId: searchIdForLog,
              searchNum,
              matches: {
                exactMatch,
                numericIdMatch,
                numericCodeMatch,
                anyMatch: exactMatch || numericIdMatch || numericCodeMatch
              }
            };
          }),
          matchingCategories: disciplineObj.categories.filter(c => {
            const cId = String(c.id || '').toLowerCase().trim();
            const cCode = String(c.code || '').toLowerCase().trim();
            const cIdNum = parseInt(cId);
            const cCodeNum = parseInt(cCode);
            const searchNum = parseInt(searchIdForLog);
            
            return (cId === searchIdForLog || cCode === searchIdForLog) ||
                   (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) ||
                   (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum);
          })
        });
      }
      
      // If not found in loaded disciplines, try static data
      if (!categoryObj && documentDisciplines.length > 0) {
        const staticDiscipline = documentDisciplines.find((d) => 
          (d.id === document.discipline) || (d.code === document.discipline)
        );
        if (staticDiscipline?.categories) {
          categoryObj = staticDiscipline.categories.find((c) => 
            String(c.id) === String(document.category) ||
            c.id === categoryParamNum ||
            c.id === document.category ||
            c.code === document.category ||
            String(c.code) === String(document.category)
          );
          
          console.log('🔍 Searching for category in staticDiscipline:', {
            document_category: document.category,
            document_category_type: typeof document.category,
            categoryParamNum,
            categoryObj,
            staticDiscipline,
            allCategories: staticDiscipline.categories?.map(c => {
              const stringMatch = String(c.id) === String(document.category);
              const numberMatch = c.id === categoryParamNum;
              const directMatch = c.id === document.category;
              const codeMatch = c.code === document.category;
              
              return { 
                id: c.id, 
                code: c.code,
                idType: typeof c.id,
                matches: {
                  stringMatch,
                  numberMatch,
                  directMatch,
                  codeMatch,
                  anyMatch: stringMatch || numberMatch || directMatch || codeMatch
                }
              };
            }),
            matchingCategories: staticDiscipline.categories?.filter(c => 
              String(c.id) === String(document.category) ||
              c.id === categoryParamNum ||
              c.id === document.category ||
              c.code === document.category ||
              String(c.code) === String(document.category)
            )
          });
        }
      }
      
      // Determine category_code for backend submission
      // document.category is category.id (for selector matching)
      // document.category_code is the original category_code from API (e.g., "kom")
      // If we found a categoryObj, we need to map its code to category_code
      let categoryCode = document.category_code; // Use stored category_code if available
      
      console.log('🔍 handleSubmit - Determining category_code:', {
        document_category: document.category,
        document_category_code: document.category_code,
        categoryObj: categoryObj,
        categoryObj_code: categoryObj?.code,
        categoryObj_id: categoryObj?.id,
        disciplineObj: disciplineObj,
        disciplineObj_id: disciplineObj?.id,
        disciplineObj_code: disciplineObj?.code,
        availableCategories: disciplineObj?.categories?.map(c => ({ id: c.id, code: c.code }))
      });
      
      // If category_code is not set, try to get it from categoryObj
      // Note: categoryObj.code (e.g., "AKAN") is NOT the same as category_code (e.g., "kom")
      // We need to map categoryObj.code to category_code using the mapping
      if (!categoryCode && categoryObj) {
        // Map category.code to category_code using the mapping
        if (categoryObj.code && categoryCodeMap[categoryObj.code]) {
          categoryCode = categoryCodeMap[categoryObj.code];
          console.log('✅ Mapped category_code from categoryObj:', {
            categoryObj_code: categoryObj.code,
            mapped_category_code: categoryCode
          });
        } else if (categoryObj.code) {
          // Fallback: try lowercase version if not in mapping
          categoryCode = categoryObj.code.toLowerCase();
          console.log('⚠️ Using lowercase fallback:', {
            categoryObj_code: categoryObj.code,
            fallback_category_code: categoryCode
          });
        }
      }
      
      // If still no categoryCode, derive from selected category (preferred) or fallback to document.category if it is a string code
      if (!categoryCode) {
        if (categoryObj?.code) {
          categoryCode = String(categoryObj.code).trim().toLowerCase();
        } else {
          categoryCode = String(document.category || "").trim().toLowerCase();
        }
      }

      // Ensure categoryCode is a non-empty string (backend validates format)
      categoryCode = String(categoryCode || "").trim().toLowerCase();
      if (!categoryCode) {
        toast.error(t("Kategori er påkrevd"));
        return;
      }
      
      // Find chapter by number
      let chapterId = null;
      if (categoryObj && document.chapter) {
        const chapter = findChapterInCategoryTree(categoryObj, document.chapter);
        chapterId = chapter?.id || null;
      }
      
      // Prepare document data for API
      // Note: status and version are handled by API, not sent from form
      const documentData = {
        title: document.title,
        description: document.description,
        category_code: categoryCode,
        document_type: document.document_type,
        content_type: document.content_type,
        content: document.content_type === "text" ? document.content : null,
        discipline_id: disciplineObj?.id || null,
        category_id: categoryObj?.id || null,
        chapter_id: chapterId,
        // Only include attached_document_id if content_type is file
        ...(document.content_type === "file" && document.attached_document_id ? {
          attached_document_id: document.attached_document_id
        } : {})
      };
      
      let createdDocumentId = id;
      
      if (isNew) {
        // Include access data in create request
        const createData = {
          ...documentData,
          access_type: document.access_type || "all",
          user_ids: document.access_type === "specific" ? document.selected_user_ids : []
        };
        
        const response = await createHseDocument(createData);
        
        // Check if response contains errors before showing success
        if (response.errors && response.errors.length > 0) {
          throw new Error(response.errors[0] || t("Kunne ikke opprette dokument"));
        }
        
        createdDocumentId = response.document?.id || response.id;
        toast.success(t("Dokument opprettet"));
        
        // Navigate to the created document view
        if (createdDocumentId) {
          navigate(`/dashboard/hse-ks/documents/${createdDocumentId}`);
          return; // Exit early to avoid double navigation
        }
      } else {
        try {
          // If creating a revision, send create_revision and change_summary as separate parameters
          const updateData = { ...documentData };
          let revisionParams = {};
          if (isRevision) {
            // Send create_revision and change_summary as top-level params, not nested in document
            revisionParams = {
              create_revision: true,
              change_summary: document.change_summary || t("Ny revisjon")
            };
            // Also add to document data for backwards compatibility
            updateData.create_revision = true;
            updateData.change_summary = revisionParams.change_summary;
          }
          const response = await updateHseDocument(id, updateData, revisionParams);
          
          // Check if response contains errors before showing success
          if (response.errors && response.errors.length > 0) {
            throw new Error(response.errors[0] || t("Kunne ikke oppdatere dokument"));
          }
          
          toast.success(isRevision ? t("Ny revisjon opprettet") : t("Dokument oppdatert"));
          
          // Update access if changed
          if (document.access_type) {
            await updateDocumentAccess(id, {
              access_type: document.access_type,
              user_ids: document.access_type === "specific" ? document.selected_user_ids : []
            });
          }
          
          // If a new revision was created, navigate to document view to see it
          if (isRevision && (response.version || response.document)) {
            // Navigate to document view - the new version will be visible there
            navigate(`/dashboard/hse-ks/documents/${id}`);
            return; // Exit early to avoid double navigation
          }
        } catch (updateErr) {
          // Check if error is about approved version requiring revision
          if (updateErr.response?.data?.requires_revision && !isRevision) {
            toast.error(t("Dette dokumentet har en godkjent versjon. Du må opprette en ny revisjon i stedet."), {
              duration: 5000
            });
            // Navigate to create revision
            navigate(`/dashboard/hse-ks/documents/${id}/edit?revision=true`);
            return;
          }
          // Extract error message from response
          const errorMessage = updateErr.response?.data?.errors?.[0] || updateErr.message || t("Kunne ikke oppdatere dokument");
          toast.error(errorMessage);
          throw updateErr;
        }
      }
      
      // Navigate back to document view after save (only for updates, new documents already navigated above)
      if (!isNew) {
        navigate(`/dashboard/hse-ks/documents/${id}`);
      }
    } catch (err) {
      console.error("Error saving document:", err);
      // Only show error toast if we haven't already shown one in the inner catch block
      if (!err.response?.data?.requires_revision) {
        const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke lagre dokument");
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const documentTypes = [
    { value: "policy", label: t("Policy") },
    { value: "procedure", label: t("Prosedyrer") },
    { value: "instruction", label: t("Instrukser") },
    { value: "form", label: t("Skjemaer") },
    { value: "guidance", label: t("Veiledninger") },
    { value: "record", label: t("Registreringer") },
    { value: "template", label: t("Maler") },
  ];

  if (!canEdit) {
    return (
      <DashboardHeader
        title={isNew ? t("Nytt dokument") : t("Rediger dokument")}
        subtitle={t("Du har ikke tilgang til å opprette/redigere dokumenter")}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl">
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <p className="text-gray-500">{t("Kontakt ledelse for tilgang")}</p>
            </div>
          </div>
        </div>
      </DashboardHeader>
    );
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste dokument")]} />;
  }

  return (
    <DashboardHeader
      title={isNew ? t("Nytt dokument") : (isRevision ? t("Ny revisjon") : t("Rediger dokument"))}
      subtitle={isRevision ? t("Opprett ny revisjon av dokumentet") : t("Dokumentskjema med versjonskontroll")}
      backButton={!isNew ? {
        label: t("Tilbake"),
        action: () => navigate(`/dashboard/hse-ks/documents/${id}`),
        icon: ArrowLeftIcon,
      } : {
        label: t("Tilbake"),
        action: () => navigate("/dashboard/hse-ks/documents"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mt-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {t("Grunnleggende informasjon")}
              </h3>
              
              <div className="space-y-4">
                {/* Classification Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {t("Klassifisering og Nummerering")}
                  </h4>
                  
                  {/* Combined Discipline/Category/Chapter Selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Fag/Bransje, Kategori og Kapittel")} <span className="text-red-500">*</span>
                    </label>
                    <DisciplineCategoryChapterSelector
                      disciplineId={document.discipline}
                      categoryId={document.category}
                      chapterNumber={document.chapter}
                      onDisciplineChange={(value) => {
                        handleChange("discipline", value);
                        handleChange("category", "");
                        handleChange("chapter", "");
                        handleChange("sub_chapter", "");
                      }}
                      onCategoryChange={(value, categoryObjFromSelector) => {
                        console.log('📝 onCategoryChange called:', {
                          value,
                          valueType: typeof value,
                          categoryObjFromSelector,
                          currentDiscipline: document.discipline,
                          disciplines: disciplines.map(d => ({ id: d.id, code: d.code }))
                        });
                        
                        handleChange("category", value);
                        
                        // If we got the category object from selector, use it directly
                        if (categoryObjFromSelector) {
                          console.log('✅ Using category object from selector:', {
                            categoryObj: categoryObjFromSelector,
                            code: categoryObjFromSelector.code,
                            categoryCodeMap_hasMapping: categoryObjFromSelector.code ? categoryCodeMap[categoryObjFromSelector.code] : false
                          });
                          
                          // Map category.code to category_code using the mapping
                        if (categoryObjFromSelector.code) {
                          const derivedCode = String(categoryObjFromSelector.code).trim().toLowerCase();
                          console.log('✅ Deriving category_code from selector:', {
                            from: categoryObjFromSelector.code,
                            to: derivedCode
                          });
                          handleChange("category_code", derivedCode);
                            handleChange("chapter", "");
                            handleChange("sub_chapter", "");
                            return; // Exit early since we got the category object
                          }
                        }
                        
                        // Find category_code from the selected category
                        // Try to find discipline first - use the one that's currently selected
                        let disciplineObj = disciplines.find((d) => 
                          String(d.id) === String(document.discipline) || 
                          d.code === document.discipline ||
                          String(d.code) === String(document.discipline)
                        );
                        
                        // If not found, try static data
                        if (!disciplineObj) {
                          disciplineObj = documentDisciplines.find((d) => 
                            String(d.id) === String(document.discipline) ||
                            d.code === document.discipline
                          );
                        }
                        
                        // Convert value to number if it's a numeric string
                        const valueNum = !isNaN(value) ? parseInt(value, 10) : null;
                        
                        const searchId = String(value || '').toLowerCase().trim();
                        console.log('🔍 Finding category object:', {
                          disciplineObj,
                          disciplineObj_id: disciplineObj?.id,
                          disciplineObj_code: disciplineObj?.code,
                          disciplineObj_name: disciplineObj?.name,
                          document_discipline: document.discipline,
                          value,
                          valueType: typeof value,
                          valueNum,
                          searchId,
                          categoriesCount: disciplineObj?.categories?.length,
                          categories: disciplineObj?.categories?.map(c => {
                            const cId = String(c.id || '').toLowerCase().trim();
                            const cCode = String(c.code || '').toLowerCase().trim();
                            
                            return { 
                              id: c.id, 
                              code: c.code,
                              name: c.name,
                              idType: typeof c.id,
                              cId,
                              cCode,
                              searchId,
                              matches: {
                                idMatch: cId === searchId,
                                codeMatch: cCode === searchId,
                                numberMatch: c.id === valueNum,
                                directMatch: c.id === value
                              }
                            };
                          }),
                          rawCategories: JSON.parse(JSON.stringify(disciplineObj?.categories || []))
                        });
                        
                        // Try to find category - use EXACT same approach as DisciplineCategoryChapterSelector
                        let selectedCategoryObj = null;
                        if (disciplineObj?.categories) {
                          const searchId = String(value || '').toLowerCase().trim();
                          selectedCategoryObj = disciplineObj.categories.find((c) => {
                            const cId = String(c.id || '').toLowerCase().trim();
                            const cCode = String(c.code || '').toLowerCase().trim();
                            
                            // Exact match (case-insensitive) - same as DisciplineCategoryChapterSelector
                            if (cId === searchId || cCode === searchId) {
                              return true;
                            }
                            
                            // Numeric match - EXACT same as DisciplineCategoryChapterSelector
                            const cIdNum = parseInt(cId);
                            const cCodeNum = parseInt(cCode);
                            const searchNum = parseInt(searchId);
                            
                            if (!isNaN(cIdNum) && !isNaN(searchNum) && cIdNum === searchNum) {
                              return true;
                            }
                            if (!isNaN(cCodeNum) && !isNaN(searchNum) && cCodeNum === searchNum) {
                              return true;
                            }
                            
                            return false;
                          });
                        }
                        
                        const searchIdForLog = String(value || '').toLowerCase().trim();
                        console.log('📋 Selected category object:', {
                          selectedCategoryObj,
                          categoryObj_id: selectedCategoryObj?.id,
                          categoryObj_code: selectedCategoryObj?.code,
                          categoryObj_name: selectedCategoryObj?.name,
                          categoryCodeMap_hasMapping: selectedCategoryObj?.code ? categoryCodeMap[selectedCategoryObj.code] : false,
                          allCategories: disciplineObj?.categories?.map(c => {
                            const cId = String(c.id || '').toLowerCase().trim();
                            const cCode = String(c.code || '').toLowerCase().trim();
                            
                            return {
                              id: c.id,
                              code: c.code,
                              name: c.name,
                              idType: typeof c.id,
                              cId,
                              cCode,
                              searchId: searchIdForLog,
                              matches: {
                                idMatch: cId === searchIdForLog,
                                codeMatch: cCode === searchIdForLog,
                                numberMatch: c.id === valueNum,
                                directMatch: c.id === value,
                                stringMatch: String(c.id) === String(value),
                                stringCodeMatch: String(c.code) === String(value),
                                anyMatch: cId === searchIdForLog || cCode === searchIdForLog || c.id === valueNum || c.id === value
                              }
                            };
                          }),
                          rawCategories: JSON.parse(JSON.stringify(disciplineObj?.categories || []))
                        });
                        
                        // Always derive category_code from selected Category.code
                        if (selectedCategoryObj?.code) {
                          const derivedCode = String(selectedCategoryObj.code).trim().toLowerCase();
                          console.log('✅ Deriving category code:', {
                            from: selectedCategoryObj.code,
                            to: derivedCode
                          });
                          handleChange("category_code", derivedCode);
                        } else if (selectedCategoryObj) {
                          console.warn('⚠️ Selected category has no code:', {
                            categoryObj: selectedCategoryObj,
                            categoryObj_id: selectedCategoryObj?.id
                          });
                          handleChange("category_code", "");
                        } else {
                          // Category not found at all
                          console.error('❌ Category object not found:', {
                            value,
                            valueNum,
                            valueType: typeof value,
                            searchId: String(value || '').toLowerCase().trim(),
                            disciplineObj,
                            disciplineObj_id: disciplineObj?.id,
                            disciplineObj_code: disciplineObj?.code,
                            allCategories: disciplineObj?.categories?.map(c => {
                              const cId = String(c.id || '').toLowerCase().trim();
                              const cCode = String(c.code || '').toLowerCase().trim();
                              const searchId = String(value || '').toLowerCase().trim();
                              
                              return {
                                id: c.id,
                                idType: typeof c.id,
                                code: c.code,
                                codeType: typeof c.code,
                                cId,
                                cCode,
                                searchId,
                                matches: {
                                  idMatch: cId === searchId,
                                  codeMatch: cCode === searchId,
                                  numberMatch: c.id === valueNum,
                                  directMatch: c.id === value,
                                  stringMatch: String(c.id) === String(value),
                                  stringCodeMatch: String(c.code) === String(value)
                                }
                              };
                            }),
                            rawCategories: disciplineObj?.categories
                          });
                          handleChange("category_code", "");
                        }
                        handleChange("chapter", "");
                        handleChange("sub_chapter", "");
                      }}
                      onChapterChange={(value) => {
                        handleChange("chapter", value);
                        handleChange("sub_chapter", "");
                      }}
                      required
                    />
                  </div>
                  
                  {/* Document Type Selector - Separate */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Dokumenttype")} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={document.document_type}
                      onChange={(e) => handleChange("document_type", e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    >
                      {documentTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Auto-generated document number */}
                  {document.document_number && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t("Dokumentnummer")} (automatisk generert)
                      </label>
                      <p className="text-sm font-mono text-blue-900">
                        {document.document_number}
                        {document.version > 1 && `-v${document.version}`}
                      </p>
                    </div>
                  )}
                </div>

                <PMField
                  label={t("Tittel")}
                  required
                  value={document.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder={t("Skriv inn dokumenttittel")}
                />

                <PMField
                  label={t("Beskrivelse")}
                  value={document.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={t("Skriv inn beskrivelse")}
                  multiline
                  rows={3}
                />

                {/* Status and Version are handled by API, not editable */}
              </div>
            </div>
          </div>

          {/* Access Control */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {t("Tilgang")}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Hvem skal ha tilgang?")}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="all"
                        checked={document.access_type === "all"}
                        onChange={(e) => handleChange("access_type", e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{t("Alle ansatte")}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="specific"
                        checked={document.access_type === "specific"}
                        onChange={(e) => handleChange("access_type", e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{t("Spesifikke ansatte")}</span>
                    </label>
                  </div>
                </div>

                {document.access_type === "specific" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Velg ansatte")}
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto border border-gray-200">
                      {companyUsers.length > 0 ? (
                        companyUsers.map((user) => (
                          <label key={user.id} className="flex items-center py-2 hover:bg-gray-100 px-2 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={document.selected_user_ids?.includes(user.id) || false}
                              onChange={(e) => {
                                const currentIds = document.selected_user_ids || [];
                                const newIds = e.target.checked
                                  ? [...currentIds, user.id]
                                  : currentIds.filter(id => id !== user.id);
                                handleChange("selected_user_ids", newIds);
                              }}
                              className="mr-3"
                            />
                            <span className="text-sm text-gray-700">{user.name || user.email}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">{t("Ingen brukere tilgjengelig")}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    {t("Alle valgte brukere får automatisk tilgang til å lese og markere dokumentet som lest/forstått.")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Document Content */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {t("Innhold")}
              </h3>
              
              {/* Content Type Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Innholdstype")} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      handleChange("content_type", "text");
                      handleChange("attached_document_id", null);
                      setUploadedDocument(null);
                    }}
                    className={`flex items-center space-x-3 p-4 border-2 rounded-lg transition-all ${
                      document.content_type === "text"
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <PencilSquareIcon className={`h-6 w-6 ${
                      document.content_type === "text" ? "text-indigo-600" : "text-gray-400"
                    }`} />
                    <div className="text-left">
                      <div className={`font-medium ${
                        document.content_type === "text" ? "text-indigo-900" : "text-gray-700"
                      }`}>
                        {t("Skriv innhold")}
                      </div>
                      <div className={`text-sm ${
                        document.content_type === "text" ? "text-indigo-600" : "text-gray-500"
                      }`}>
                        {t("Skriv dokumentinnhold direkte i editoren")}
                      </div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      handleChange("content_type", "file");
                      handleChange("content", "");
                    }}
                    className={`flex items-center space-x-3 p-4 border-2 rounded-lg transition-all ${
                      document.content_type === "file"
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <DocumentArrowUpIcon className={`h-6 w-6 ${
                      document.content_type === "file" ? "text-indigo-600" : "text-gray-400"
                    }`} />
                    <div className="text-left">
                      <div className={`font-medium ${
                        document.content_type === "file" ? "text-indigo-900" : "text-gray-700"
                      }`}>
                        {t("Last opp dokument")}
                      </div>
                      <div className={`text-sm ${
                        document.content_type === "file" ? "text-indigo-600" : "text-gray-500"
                      }`}>
                        {t("Last opp PDF, Word eller annet dokument")}
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Text Content Editor */}
              {document.content_type === "text" && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("Dokumentinnhold")}
                    </label>
                    <AIGenerator
                      module="hse_document"
                      context={{
                        title: document.title,
                        description: document.description,
                        discipline: disciplines.find(d => 
                          String(d.id) === String(document.discipline) || 
                          d.code === document.discipline
                        )?.name,
                        category: disciplines
                          .find(d => String(d.id) === String(document.discipline) || d.code === document.discipline)
                          ?.categories?.find(c => 
                            String(c.id) === String(document.category) || c.code === document.category
                          )?.name,
                        chapter: document.chapter || document.sub_chapter,
                        document_type: document.document_type
                      }}
                      fieldsToGenerate={['content']}
                      onGenerate={(generatedFields) => {
                        if (generatedFields.content) {
                          handleChange("content", generatedFields.content);
                          toast.success(t("Innhold generert og lagt til"));
                        }
                      }}
                      config={aiGeneratorConfigs.hse_document}
                      className="text-xs"
                    />
                  </div>
                  <PromangRichTextEditor
                    value={document.content}
                    onChange={(value) => handleChange("content", value)}
                    placeholder={t("Skriv inn dokumentinnhold...")}
                    size="lg"
                    tablesEnabled
                  />
                </div>
              )}

              {/* File Upload Section */}
              {document.content_type === "file" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Last opp dokument")}
                  </label>
                  <DocumentUploadSectionV6
                    module="hse_document"
                    moduleParam={id || document.document_number || "temp"}
                    multiple={false}
                    buttonStyle="default"
                    allowedFileTypes={HSE_DOCUMENT_ALLOWED_FILE_TYPES}
                    parentCallback={(uploadedFiles) => {
                      if (uploadedFiles && uploadedFiles.length > 0) {
                        const uploadedDoc = uploadedFiles[0];
                        setUploadedDocument(uploadedDoc);
                        handleChange("attached_document_id", uploadedDoc.id);
                        toast.success(t("Dokument lastet opp"));
                      }
                    }}
                  />
                  
                  {uploadedDocument && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <CheckIcon className="h-5 w-5 text-green-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-900">
                            {uploadedDocument.title || uploadedDocument.document_file_name || uploadedDocument.file?.filename || t("Dokument lastet opp")}
                          </p>
                          {(uploadedDocument.document_file_name || uploadedDocument.file?.filename) && (
                            <p className="text-xs text-green-700 mt-1">
                              {uploadedDocument.document_file_name || uploadedDocument.file?.filename}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedDocument(null);
                            handleChange("attached_document_id", null);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {!uploadedDocument && (
                    <p className="mt-2 text-sm text-gray-500">
                      {t("Last opp PDF, Word-dokument eller annen fil")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 bg-white shadow sm:rounded-lg px-4 py-5 sm:p-6">
            <button
              type="button"
              onClick={() => navigate("/dashboard/hse-ks/documents")}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              {t("Avbryt")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              {saving ? t("Lagrer...") : (isNew ? t("Opprett") : t("Lagre"))}
            </button>
          </div>
        </form>
      </div>
    </DashboardHeader>
  );
};
